import React, { useState, useEffect } from 'react';
import { Package, ShoppingCart, Users, TrendingUp, TrendingDown, Clock, DollarSign, BarChart3 } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Stats {
  totalProdutos: number;
  totalPedidos: number;
  totalUsuarios: number;
  vendasHoje: number;
}

interface MetricasAvancadas {
  hoje: {
    total_pedidos: number;
    total_vendas: number;
    ticket_medio: number;
  };
  ontem: {
    total_pedidos: number;
    total_vendas: number;
    ticket_medio: number;
  };
  ultimos7Dias: {
    total_pedidos: number;
    total_vendas: number;
    ticket_medio: number;
  };
  ultimos30Dias: {
    total_pedidos: number;
    total_vendas: number;
    ticket_medio: number;
  };
  crescimento: {
    pedidos: number;
    vendas: number;
  };
  produtos_mais_vendidos: Array<{
    nome: string;
    quantidade_vendida: number;
    receita: number;
  }>;
}

interface PedidoRecente {
  id: number;
  nome_completo_cliente: string;
  total: number | string;
  status: string;
  created_at: string;
  total_itens: number;
}

const DashboardHome: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    totalProdutos: 0,
    totalPedidos: 0,
    totalUsuarios: 0,
    vendasHoje: 0
  });
  const [metricas, setMetricas] = useState<MetricasAvancadas | null>(null);
  const [pedidosRecentes, setPedidosRecentes] = useState<PedidoRecente[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchMetricasAvancadas();
    fetchPedidosRecentes();
    
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(() => {
      fetchMetricasAvancadas();
      fetchPedidosRecentes();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const [produtosRes, pedidosRes, usuariosRes] = await Promise.all([
        api.get('/api/produtos'),
        api.get('/api/pedidos'),
        api.get('/api/users')
      ]);

      const hoje = new Date().toISOString().split('T')[0];
      const vendasHoje = pedidosRes.data
        .filter((pedido: any) => pedido.created_at?.startsWith(hoje))
        .reduce((total: number, pedido: any) => total + pedido.total, 0);

      setStats({
        totalProdutos: produtosRes.data.length,
        totalPedidos: pedidosRes.data.length,
        totalUsuarios: usuariosRes.data?.length || 0,
        vendasHoje
      });
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricasAvancadas = async () => {
    try {
      const response = await api.get('/api/dashboard/metricas');
      setMetricas(response.data);
    } catch (error) {
      console.error('Erro ao carregar métricas avançadas:', error);
    }
  };

  const fetchPedidosRecentes = async () => {
    try {
      const response = await api.get('/api/pedidos/recentes');
      setPedidosRecentes(response.data);
    } catch (error) {
      console.error('Erro ao carregar pedidos recentes:', error);
    }
  };

  const statCards = [
    {
      name: 'Total de Produtos',
      value: stats.totalProdutos,
      icon: Package,
      color: 'bg-blue-500',
      textColor: 'text-blue-500'
    },
    {
      name: 'Total de Pedidos',
      value: stats.totalPedidos,
      icon: ShoppingCart,
      color: 'bg-green-500',
      textColor: 'text-green-500'
    },
    {
      name: 'Total de Usuários',
      value: stats.totalUsuarios,
      icon: Users,
      color: 'bg-purple-500',
      textColor: 'text-purple-500'
    },
    {
      name: 'Vendas Hoje',
      value: `R$ ${stats.vendasHoje.toFixed(2)}`,
      icon: TrendingUp,
      color: 'bg-orange-500',
      textColor: 'text-orange-500'
    }
  ];

  const metricasCards = metricas ? [
    {
      name: 'Pedidos Hoje',
      value: metricas.hoje.total_pedidos,
      subtitle: `vs ${metricas.ontem.total_pedidos} ontem`,
      icon: ShoppingCart,
      color: 'bg-green-500',
      textColor: 'text-green-500',
      crescimento: metricas.crescimento.pedidos
    },
    {
      name: 'Vendas Hoje',
      value: `R$ ${metricas.hoje.total_vendas.toFixed(2)}`,
      subtitle: `vs R$ ${metricas.ontem.total_vendas.toFixed(2)} ontem`,
      icon: DollarSign,
      color: 'bg-blue-500',
      textColor: 'text-blue-500',
      crescimento: metricas.crescimento.vendas
    },
    {
      name: 'Ticket Médio',
      value: `R$ ${metricas.hoje.ticket_medio.toFixed(2)}`,
      subtitle: `últimos 7 dias: R$ ${metricas.ultimos7Dias.ticket_medio.toFixed(2)}`,
      icon: BarChart3,
      color: 'bg-purple-500',
      textColor: 'text-purple-500'
    },
    {
      name: 'Últimos 7 Dias',
      value: metricas.ultimos7Dias.total_pedidos,
      subtitle: `R$ ${metricas.ultimos7Dias.total_vendas.toFixed(2)} em vendas`,
      icon: Clock,
      color: 'bg-orange-500',
      textColor: 'text-orange-500'
    }
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Visão geral do seu negócio
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center ${stat.color}`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Métricas Avançadas */}
      {metricasCards.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Métricas de Performance
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {metricasCards.map((metric) => {
              const Icon = metric.icon;
              const isPositive = metric.crescimento && metric.crescimento > 0;
              const isNegative = metric.crescimento && metric.crescimento < 0;
              
              return (
                <div
                  key={metric.name}
                  className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
                >
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${metric.color}`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            {metric.name}
                          </dt>
                          <dd className="text-lg font-medium text-gray-900 dark:text-white">
                            {metric.value}
                          </dd>
                          <dd className="text-xs text-gray-500 dark:text-gray-400">
                            {metric.subtitle}
                          </dd>
                          {metric.crescimento !== undefined && (
                            <dd className="flex items-center text-xs mt-1">
                              {isPositive ? (
                                <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                              ) : isNegative ? (
                                <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                              ) : null}
                              <span className={isPositive ? 'text-green-500' : isNegative ? 'text-red-500' : 'text-gray-500'}>
                                {metric.crescimento > 0 ? '+' : ''}{metric.crescimento}%
                              </span>
                            </dd>
                          )}
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pedidos Recentes */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Pedidos Recentes
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              Atualizado em tempo real
            </span>
          </div>
          <div className="mt-5">
            {pedidosRecentes.length > 0 ? (
              <div className="flow-root">
                <ul className="-mb-8">
                  {pedidosRecentes.map((pedido, index) => {
                    const isLast = index === pedidosRecentes.length - 1;
                    const statusColors = {
                      'pendente': 'bg-yellow-500',
                      'confirmado': 'bg-blue-500',
                      'preparando': 'bg-orange-500',
                      'saiu_para_entrega': 'bg-purple-500',
                      'entregue': 'bg-green-500',
                      'cancelado': 'bg-red-500'
                    };
                    
                    const statusLabels = {
                      'pendente': 'Pendente',
                      'confirmado': 'Confirmado',
                      'preparando': 'Preparando',
                      'saiu_para_entrega': 'Saiu para Entrega',
                      'entregue': 'Entregue',
                      'cancelado': 'Cancelado'
                    };

                    const formatTime = (dateString: string) => {
                      const date = new Date(dateString);
                      const now = new Date();
                      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
                      
                      if (diffInMinutes < 1) return 'Agora mesmo';
                      if (diffInMinutes < 60) return `${diffInMinutes}min atrás`;
                      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`;
                      return date.toLocaleDateString('pt-BR');
                    };

                    return (
                      <li key={pedido.id}>
                        <div className={`relative ${!isLast ? 'pb-8' : ''}`}>
                          {!isLast && (
                            <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                          )}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white dark:ring-gray-800 ${statusColors[pedido.status as keyof typeof statusColors] || 'bg-gray-500'}`}>
                                <ShoppingCart className="h-5 w-5 text-white" />
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  <span className="font-medium text-gray-900 dark:text-white">{pedido.nome_completo_cliente}</span> fez um pedido
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {pedido.total_itens} itens • R$ {Number(pedido.total).toFixed(2)} • 
                                  <span className={`ml-1 font-medium ${
                                    pedido.status === 'entregue' ? 'text-green-600 dark:text-green-400' :
                                    pedido.status === 'cancelado' ? 'text-red-600 dark:text-red-400' :
                                    'text-blue-600 dark:text-blue-400'
                                  }`}>
                                    {statusLabels[pedido.status as keyof typeof statusLabels] || pedido.status}
                                  </span>
                                </p>
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                                <time dateTime={pedido.created_at}>
                                  {formatTime(pedido.created_at)}
                                </time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className="text-center py-8">
                <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Nenhum pedido recente</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Os pedidos aparecerão aqui em tempo real.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
